import { supabase } from '../lib/supabase';

interface NotificationSettings {
  dailyReminder: boolean;
  flrWarning: boolean;
  receivableReminder: boolean;
}

// 毎日23:00の通知スケジューラー
export function scheduleDailyReminder(userId: string, settings: NotificationSettings) {
  if (!settings.dailyReminder) {
    return;
  }

  const now = new Date();
  const targetTime = new Date(now);
  targetTime.setHours(23, 0, 0, 0);

  // 既に23:00を過ぎている場合は翌日の23:00に設定
  if (now >= targetTime) {
    targetTime.setDate(targetTime.getDate() + 1);
  }

  const delay = targetTime.getTime() - now.getTime();

  setTimeout(async () => {
    await checkAndNotifyDailyInput(userId);
    // 翌日の通知をスケジュール（24時間後）
    setTimeout(() => scheduleDailyReminder(userId, settings), 24 * 60 * 60 * 1000);
  }, delay);
}

// 当日の入力チェックと通知
async function checkAndNotifyDailyInput(userId: string) {
  try {
    const today = new Date().toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .from('expense_logs')
      .select('id')
      .eq('user_id', userId)
      .gte('expense_date', today)
      .lt('expense_date', new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0])
      .maybeSingle();

    if (error) {
      console.error('Error checking daily input:', error);
      return;
    }

    // 今日の入力がない場合は通知
    if (!data) {
      sendNotification({
        title: '入力忘れ防止',
        body: '今日のレシート撮影はお済みですか？',
        tag: 'daily-reminder',
        requireInteraction: true,
      });
    }
  } catch (error) {
    console.error('Error in checkAndNotifyDailyInput:', error);
  }
}

// FLR警告通知
export async function checkAndNotifyFLRWarning(userId: string, settings: NotificationSettings) {
  if (!settings.flrWarning) {
    return;
  }

  try {
    // 当月のデータを取得
    const currentMonth = new Date().toISOString().slice(0, 7);
    
    const { data: monthlyData, error } = await supabase
      .from('monthly_data')
      .select('*')
      .eq('user_id', userId)
      .eq('month', currentMonth)
      .maybeSingle();

    if (error || !monthlyData) {
      return;
    }

    const revenue = monthlyData.revenue || 0;
    if (revenue === 0) return;

    const foodCost = monthlyData.food_cost || 0;
    const laborCost = monthlyData.labor_cost || 0;
    
    const foodRatio = (foodCost / revenue) * 100;
    const laborRatio = (laborCost / revenue) * 100;

    // 目標比率を超えた場合に通知
    const targetFoodRatio = monthlyData.target_food_ratio || 30;
    const targetLaborRatio = monthlyData.target_labor_ratio || 30;

    if (foodRatio > targetFoodRatio || laborRatio > targetLaborRatio) {
      const warnings = [];
      if (foodRatio > targetFoodRatio) {
        warnings.push(`仕入れ ${foodRatio.toFixed(1)}%（目標${targetFoodRatio}%）`);
      }
      if (laborRatio > targetLaborRatio) {
        warnings.push(`人件費 ${laborRatio.toFixed(1)}%（目標${targetLaborRatio}%）`);
      }

      sendNotification({
        title: '⚠️ 経営危険信号',
        body: `${warnings.join('、')}が目標を超えています！`,
        tag: 'flr-warning',
        requireInteraction: true,
      });
    }
  } catch (error) {
    console.error('Error in checkAndNotifyFLRWarning:', error);
  }
}

// 入金リマインド通知
export async function checkAndNotifyReceivableReminder(userId: string, settings: NotificationSettings) {
  if (!settings.receivableReminder) {
    return;
  }

  try {
    const today = new Date().toISOString().split('T')[0];
    
    const { data: receivables, error } = await supabase
      .from('expense_logs')
      .select('*')
      .eq('user_id', userId)
      .eq('payment_method', 'payable')
      .eq('due_date', today)
      .is('paid_at', null);

    if (error) {
      console.error('Error checking receivables:', error);
      return;
    }

    if (receivables && receivables.length > 0) {
      const totalAmount = receivables.reduce((sum, r) => sum + (r.amount || 0), 0);
      
      sendNotification({
        title: '💰 入金リマインド',
        body: `本日入金予定の売掛金があります（${receivables.length}件、合計¥${totalAmount.toLocaleString()}）。通帳の確認をお願いします。`,
        tag: 'receivable-reminder',
        requireInteraction: true,
      });
    }
  } catch (error) {
    console.error('Error in checkAndNotifyReceivableReminder:', error);
  }
}

// 通知送信ヘルパー
interface NotificationOptions {
  title: string;
  body: string;
  tag: string;
  requireInteraction?: boolean;
  icon?: string;
  badge?: string;
}

function sendNotification(options: NotificationOptions) {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return;
  }

  if (Notification.permission !== 'granted') {
    console.warn('Notification permission not granted');
    return;
  }

  // Service Workerが利用可能な場合
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.showNotification(options.title, {
        body: options.body,
        icon: options.icon || '/icon-192x192.png',
        badge: options.badge || '/badge-72x72.png',
        tag: options.tag,
        requireInteraction: options.requireInteraction || false,
        vibrate: [200, 100, 200],
        data: {
          url: '/',
          timestamp: Date.now(),
        },
      });
    });
  } else {
    // フォールバック：通常の通知
    new Notification(options.title, {
      body: options.body,
      icon: options.icon || '/icon-192x192.png',
      tag: options.tag,
      requireInteraction: options.requireInteraction || false,
    });
  }
}

// 通知スケジューラーの初期化
export function initializeNotificationScheduler(userId: string) {
  const storageKey = `notification_settings_${userId}`;
  const savedSettings = localStorage.getItem(storageKey);
  
  if (!savedSettings) {
    return;
  }

  try {
    const settings: NotificationSettings = JSON.parse(savedSettings);
    
    // 毎日23:00の通知をスケジュール
    if (settings.dailyReminder) {
      scheduleDailyReminder(userId, settings);
    }

    // FLR警告は1時間ごとにチェック
    if (settings.flrWarning) {
      setInterval(() => {
        checkAndNotifyFLRWarning(userId, settings);
      }, 60 * 60 * 1000);
    }

    // 入金リマインドは毎朝9:00にチェック
    if (settings.receivableReminder) {
      const scheduleReceivableCheck = () => {
        const now = new Date();
        const targetTime = new Date(now);
        targetTime.setHours(9, 0, 0, 0);

        if (now >= targetTime) {
          targetTime.setDate(targetTime.getDate() + 1);
        }

        const delay = targetTime.getTime() - now.getTime();

        setTimeout(() => {
          checkAndNotifyReceivableReminder(userId, settings);
          // 翌日の通知をスケジュール
          setTimeout(scheduleReceivableCheck, 24 * 60 * 60 * 1000);
        }, delay);
      };

      scheduleReceivableCheck();
    }
  } catch (error) {
    console.error('Error initializing notification scheduler:', error);
  }
}